import React from "react";

/**
 * Mobile-drawer state for the back-office shell. On phones (<=760px) the
 * sidebar is an off-canvas drawer toggled by a hamburger in the topbar; on
 * desktop it's a fixed rail and this state is inert. Drawer state is ephemeral
 * (always starts closed) and closes on navigation or scrim tap.
 */

const MOBILE_QUERY = "(max-width: 760px)";

interface SidebarState {
  mobileOpen: boolean;
  isMobile: boolean;
  toggle: () => void;
  close: () => void;
}

const SidebarContext = React.createContext<SidebarState | null>(null);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const toggle = React.useCallback(() => setMobileOpen((o) => !o), []);
  const close = React.useCallback(() => setMobileOpen(false), []);

  const value = React.useMemo<SidebarState>(
    () => ({ mobileOpen, isMobile, toggle, close }),
    [mobileOpen, isMobile, toggle, close]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarState {
  const ctx = React.useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within a SidebarProvider");
  return ctx;
}
